import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { useAuth } from "../../../AuthContext";
import {
  FaCalendarAlt,
  FaGavel,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaUserTie,
  FaClock,
  FaMapMarkerAlt,
  FaCheckCircle,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const SessoesPlenarias = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [sessoes, setSessoes] = useState([]);
  const [legislaturas, setLegislaturas] = useState([]);
  const [legislaturaSelecionada, setLegislaturaSelecionada] = useState(null);
  const [parlamentares, setParlamentares] = useState([]);
  const [tiposSessao, setTiposSessao] = useState([]);
  const [statusSessao, setStatusSessao] = useState([]);

  // Estados de filtro
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const [statusSelecionado, setStatusSelecionado] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Estados do modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoSessao, setEditandoSessao] = useState(null);
  const [formData, setFormData] = useState({
    legislatura_id: null,
    numero: "",
    tipo: "ordinaria",
    data: new Date(),
    hora_inicio: "", // mudar de null para string vazia
    hora_fim: "", // mudar de null para string vazia
    status: "agendada",
    presidente_id: null,
    secretario_id: null,
    local: "",
    pauta_aprovada: false,
    ata: "",
    observacoes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Modal de visualização
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [sessaoSelecionada, setSessaoSelecionada] = useState(null);

  // Anos para dropdown
  const anos = Array.from({ length: 10 }, (_, i) => {
    const ano = new Date().getFullYear() - 5 + i;
    return { label: ano.toString(), value: ano };
  });

  // Função genérica para requisições
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar configurações
  const carregarConfiguracoes = useCallback(async () => {
    try {
      const [tiposRes, statusRes] = await Promise.all([
        apiRequest("/tipos-sessao"),
        apiRequest("/status-sessao"),
      ]);
      setTiposSessao(tiposRes.data || []);
      setStatusSessao(statusRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  }, [user?.token]);

  // Carregar legislaturas
  const carregarLegislaturas = useCallback(async () => {
    try {
      const response = await apiRequest("/legislaturas");
      const legislaturasList = response.data.map((leg) => ({
        id: leg.id,
        name: leg.descricao,
        atual: leg.ativa === 1,
      }));
      setLegislaturas(legislaturasList);

      const atual = legislaturasList.find((l) => l.atual);
      if (atual) setLegislaturaSelecionada(atual);
      else if (legislaturasList[0])
        setLegislaturaSelecionada(legislaturasList[0]);
    } catch (error) {
      console.error("Erro ao carregar legislaturas:", error);
    }
  }, [user?.token]);

  // Carregar parlamentares
  const carregarParlamentares = useCallback(async () => {
    try {
      let url = "/parlamentares?ativo=true";
      if (legislaturaSelecionada?.id) {
        url += `&legislatura_id=${legislaturaSelecionada.id}`;
      }
      const response = await apiRequest(url);
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
    }
  }, [user?.token, legislaturaSelecionada]);

  // Carregar sessões
  const carregarSessoes = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/sessoes";
      const params = new URLSearchParams();

      if (termoPesquisa) params.append("search", termoPesquisa);
      if (tipoSelecionado) params.append("tipo", tipoSelecionado);
      if (statusSelecionado) params.append("status", statusSelecionado);
      if (legislaturaSelecionada?.id)
        params.append("legislatura_id", legislaturaSelecionada.id);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await apiRequest(url);
      setSessoes(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar sessões:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as sessões",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    user?.token,
    termoPesquisa,
    tipoSelecionado,
    statusSelecionado,
    legislaturaSelecionada,
  ]);

  // Efeitos
  useEffect(() => {
    if (user?.token) {
      carregarConfiguracoes();
      carregarLegislaturas();
    }
  }, [user?.token, carregarConfiguracoes, carregarLegislaturas]);

  useEffect(() => {
    if (user?.token && legislaturaSelecionada) {
      carregarParlamentares();
      carregarSessoes();
    }
  }, [
    user?.token,
    legislaturaSelecionada,
    carregarParlamentares,
    carregarSessoes,
  ]);

  // Abrir modal para criar
  const abrirModalCriar = () => {
    setEditandoSessao(null);
    setFormData({
      legislatura_id: legislaturaSelecionada?.id || null,
      numero: "",
      tipo: "ordinaria",
      data: new Date(),
      hora_inicio: "", // string vazia
      hora_fim: "", // string vazia
      status: "agendada",
      presidente_id: null,
      secretario_id: null,
      local: "",
      pauta_aprovada: false,
      ata: "",
      observacoes: "",
    });
    setModalVisible(true);
  };

  // Abrir modal para editar
  const abrirModalEditar = (sessao) => {
    const tipoObj = tiposSessao.find((t) => t.valor === sessao.tipo);
    const statusObj = statusSessao.find((s) => s.valor === sessao.status);

    setEditandoSessao(sessao);
    setFormData({
      legislatura_id: sessao.legislatura_id || legislaturaSelecionada?.id,
      numero: sessao.numero || "",
      tipo: tipoObj || sessao.tipo,
      data: sessao.data ? new Date(sessao.data) : new Date(),
      hora_inicio: sessao.hora_inicio || "", // string vazia se null
      hora_fim: sessao.hora_fim || "", // string vazia se null
      status: statusObj || sessao.status,
      presidente_id: sessao.presidente_id || null,
      secretario_id: sessao.secretario_id || null,
      local: sessao.local || "",
      pauta_aprovada: sessao.pauta_aprovada === 1,
      ata: sessao.ata || "",
      observacoes: sessao.observacoes || "",
    });
    setModalVisible(true);
  };

  // Abrir visualização
  const abrirVisualizacao = (sessao) => {
    setSessaoSelecionada(sessao);
    setViewModalVisible(true);
  };

  // Salvar sessão
  const salvarSessao = async () => {
    if (!formData.numero) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O número da sessão é obrigatório",
        life: 3000,
      });
      return;
    }

    if (!formData.data) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "A data da sessão é obrigatória",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        legislatura_id: formData.legislatura_id || legislaturaSelecionada?.id,
        numero: parseInt(formData.numero),
        tipo: formData.tipo?.valor || formData.tipo,
        data: formData.data.toISOString().split("T")[0],
        hora_inicio: formData.hora_inicio || null, // envia null se vazio
        hora_fim: formData.hora_fim || null, // envia null se vazio
        status: formData.status?.valor || formData.status,
        presidente_id: formData.presidente_id || null,
        secretario_id: formData.secretario_id || null,
        local: formData.local,
        pauta_aprovada: formData.pauta_aprovada ? 1 : 0,
        ata: formData.ata,
        observacoes: formData.observacoes,
      };

      console.log("Payload:", payload); // Debug

      if (editandoSessao) {
        await apiRequest(`/sessoes/${editandoSessao.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Sessão atualizada com sucesso",
          life: 3000,
        });
      } else {
        await apiRequest("/sessoes", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Sessão criada com sucesso",
          life: 3000,
        });
      }

      setModalVisible(false);
      carregarSessoes();
    } catch (error) {
      console.error("Erro ao salvar sessão:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao salvar sessão",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Excluir sessão
  const confirmarExclusao = (sessao) => {
  confirmDialog({
    header: "",
    style: { width: "350px" },
    className: "custom-confirm-dialog",

    message: (
      <div className="flex flex-column align-items-center text-center">
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
        ></i>

        <span
          style={{
            fontSize: "1.1rem",
            fontWeight: "500",
            marginBottom: "0.5rem",
          }}
        >
          Confirmar exclusão
        </span>

        <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
          Deseja realmente <strong>excluir</strong> a sessão
        </span>

        <span
          style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
        >
          Sessão nº {sessao.numero} -{" "}
          {new Date(sessao.data).toLocaleDateString("pt-BR")}?
        </span>
      </div>
    ),

    footer: (options) => (
      <div className="flex justify-content-center gap-2 w-full">
        <button
          className="p-button p-component p-button-sm p-button-secondary"
          onClick={options.reject}
        >
          <span className="p-button-label">Cancelar</span>
        </button>

        <button
          className="p-button p-component p-button-sm p-button-danger"
          onClick={options.accept}
        >
          <span className="p-button-label">Excluir</span>
        </button>
      </div>
    ),

    accept: async () => {
      try {
        await apiRequest(`/sessoes/${sessao.id}`, { method: "DELETE" });
        toast.current?.show({
          severity: "success",
          summary: "Excluído",
          detail: `Sessão nº ${sessao.numero} removida com sucesso`,
          life: 3000,
        });
        carregarSessoes();
      } catch (error) {
        console.error("Erro ao excluir sessão:", error);
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: error.data?.message || "Falha ao excluir sessão",
          life: 5000,
        });
      }
    },
  });
};

  // Templates da tabela - Com colunas separadas
  const numeroBody = (rowData) => (
    <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
      Sessão nº {rowData.numero}
    </span>
  );

  const dataBody = (rowData) => (
    <span style={{ fontSize: "0.9rem" }}>
      {new Date(rowData.data).toLocaleDateString("pt-BR")}
    </span>
  );

  const horaInicioBody = (rowData) => (
    <span style={{ fontSize: "0.9rem" }}>{rowData.hora_inicio || "---"}</span>
  );

  const horaFimBody = (rowData) => (
    <span style={{ fontSize: "0.9rem" }}>{rowData.hora_fim || "---"}</span>
  );

  const tipoBody = (rowData) => {
    const tipo = tiposSessao.find((t) => t.valor === rowData.tipo);
    return <Tag value={tipo?.label || rowData.tipo} severity="info" />;
  };

  const statusBody = (rowData) => {
    const status = statusSessao.find((s) => s.valor === rowData.status);
    return (
      <Tag
        value={status?.label || rowData.status}
        severity={status?.severity || "secondary"}
      />
    );
  };

  const pautaAprovadaBody = (rowData) => {
  if (rowData.status !== 'encerrada') {
    return <Tag value="Pendente" severity="warning" icon="pi pi-clock" />;
  }
  return (
    <Tag
      value={rowData.pauta_aprovada === 1 ? "Aprovada" : "Rejeitada"}
      severity={rowData.pauta_aprovada === 1 ? "success" : "danger"}
      icon={rowData.pauta_aprovada === 1 ? "pi pi-check-circle" : "pi pi-times-circle"}
    />
  );
};

  const presidenteBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
        }}
      >
        <FaUserTie size={14} />
      </div>
      <div>
        <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
          {rowData.presidente_nome || "Não definido"}
        </div>
      </div>
    </div>
  );

  const localBody = (rowData) => (
    <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
      {rowData.local || "---"}
    </span>
  );

  const acoesBody = (rowData) => (
    <React.Fragment>
      <Button
        icon="pi pi-eye"
        size="small"
        severity="info"
        tooltip="Visualizar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirVisualizacao(rowData)}
      />
      <Button
        icon="pi pi-file-edit"
        size="small"
        severity="success"
        tooltip="Editar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirModalEditar(rowData)}
      />
      <Button
        icon="pi pi-trash"
        size="small"
        severity="danger"
        tooltip="Excluir"
        tooltipOptions={{ position: "left" }}
        className="custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => confirmarExclusao(rowData)}
      />
    </React.Fragment>
  );

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        color: "#6b7280",
      }}
    >
      <FaGavel size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ marginBottom: "8px", color: "#374151", fontWeight: "500" }}>
        Nenhuma sessão encontrada
      </h3>
      <p
        style={{
          textAlign: "center",
          maxWidth: "400px",
          fontSize: "0.85rem",
          lineHeight: "1.5",
        }}
      >
        Não há sessões plenárias para a legislatura selecionada.
        <br />
        Clique em "Adicionar" para criar uma nova sessão.
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalVisible(false)}
        outlined
      />
      <Button
        label="Salvar"
        severity="success"
        icon="pi pi-check"
        onClick={salvarSessao}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Sessões Plenárias</h1>
        <p>Gestão das sessões da câmara municipal</p>
      </div>

      <div className="page-content">
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr",
                gap: "24px",
                alignItems: "end",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label style={{ fontWeight: "500" }}>
                  Escolha uma Legislatura
                </label>
                <Dropdown
                  value={legislaturaSelecionada}
                  options={legislaturas}
                  onChange={(e) => setLegislaturaSelecionada(e.value)}
                  optionLabel="name"
                  placeholder="Selecione uma legislatura"
                />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Button
                  label={mostrarFiltros ? "Ocultar Filtros" : "Mostrar Filtros"}
                  icon={mostrarFiltros ? <FaChevronUp /> : <FaChevronDown />}
                  severity="secondary"
                  outlined
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Filtros de busca */}
        {mostrarFiltros && (
          <Card style={{ padding: "0", marginBottom: "10px" }}>
            <div style={{ padding: "0 10px" }}>
              <h2 style={{ margin: 0, color: "#374151" }}>Filtros</h2>
            </div>

            <div style={{ padding: "0 10px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Buscar por texto
                  </label>
                  <InputText
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                    placeholder="Número ou local..."
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Tipo
                  </label>
                  <Dropdown
                    value={tipoSelecionado}
                    options={tiposSessao}
                    onChange={(e) => setTipoSelecionado(e.value)}
                    optionLabel="label"
                    optionValue="valor"
                    placeholder="Todos"
                    showClear
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Status
                  </label>
                  <Dropdown
                    value={statusSelecionado}
                    options={statusSessao}
                    onChange={(e) => setStatusSelecionado(e.value)}
                    optionLabel="label"
                    optionValue="valor"
                    placeholder="Todos"
                    showClear
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card style={{ padding: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "10px",
            }}
          >
            <Button
              label="Adicionar"
              icon="pi pi-plus"
              severity="success"
              size="small"
              onClick={abrirModalCriar}
              disabled={loading}
            />
            <Button
              label="Exportar"
              icon="pi pi-file-excel"
              severity="secondary"
              size="small"
              outlined
            />
          </div>

          <div style={{ padding: "0 0 10px 0" }}>
            <Message
              severity="info"
              text={`Legislatura: ${legislaturaSelecionada?.name || "Selecione"} • Total de Sessões: ${sessoes.length}`}
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={sessoes}
              dataKey="id"
              style={{ width: "100%" }}
              paginator
              rows={10}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} a {last} de {totalRecords}"
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                header="Nº Sessão"
                body={numeroBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Data"
                body={dataBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Hr Início"
                body={horaInicioBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Hr Fim"
                body={horaFimBody}
                sortable
                style={{ width: "80px" }}
              />
              <Column
                header="Tipo"
                body={tipoBody}
                sortable
                style={{ width: "150px" }}
              />
              <Column
                header="Status"
                body={statusBody}
                sortable
                style={{ width: "90px" }}
              />
              <Column 
                header="Resultado Pauta" 
                body={pautaAprovadaBody} 
                sortable 
                style={{ width: "90px" }}
              />              
              <Column
                header="Presidente"
                body={presidenteBody}
                sortable
                style={{ width: "300px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "160px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Criar/Editar */}
      <Dialog
        visible={modalVisible}
        style={{ width: "950px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVisible(false)}
        footer={modalFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i
                className={`pi ${editandoSessao ? "pi-calendar-plus" : "pi-calendar"} text-primary text-xl`}
              ></i>
            </div>
            <span className="font-bold text-xl">
              {editandoSessao ? "Editar Sessão" : "Nova Sessão"}
            </span>
          </div>
        }
      >
        <TabView className="tabview-colada">
          <TabPanel header="Dados Básicos" headerClassName="tab-header-colada">
            <div
              className="grid p-fluid"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #e8e8e8",
                borderTop: "none",
                padding: "15px",
                borderRadius: "0 0 12px 12px",
                margin: 0,
              }}
            >
              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">
                    Número da Sessão *
                  </label>
                  <InputText
                    value={formData.numero}
                    onChange={(e) =>
                      setFormData({ ...formData, numero: e.target.value })
                    }
                    placeholder="Número da sessão"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Tipo</label>
                  <Dropdown
                    value={formData.tipo}
                    options={tiposSessao}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo: e.value })
                    }
                    optionLabel="label"
                    placeholder="Selecione o tipo"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Data *</label>
                  <Calendar
                    value={formData.data}
                    onChange={(e) =>
                      setFormData({ ...formData, data: e.value })
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/aaaa"
                    showIcon
                    showButtonBar
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Status</label>
                  <Dropdown
                    value={formData.status}
                    options={statusSessao}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.value })
                    }
                    optionLabel="label"
                    placeholder="Selecione o status"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Hora Início</label>
                  <InputText
                    value={formData.hora_inicio}
                    onChange={(e) =>
                      setFormData({ ...formData, hora_inicio: e.target.value })
                    }
                    placeholder="19:00"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Hora Fim</label>
                  <InputText
                    value={formData.hora_fim}
                    onChange={(e) =>
                      setFormData({ ...formData, hora_fim: e.target.value })
                    }
                    placeholder="22:00"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Local</label>
                  <InputText
                    value={formData.local}
                    onChange={(e) =>
                      setFormData({ ...formData, local: e.target.value })
                    }
                    placeholder="Plenário Vereador..."
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Mesa Diretora" headerClassName="tab-header-colada">
            <div
              className="grid p-fluid"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #e8e8e8",
                borderTop: "none",
                padding: "15px",
                borderRadius: "0 0 12px 12px",
                margin: 0,
              }}
            >
              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Presidente</label>
                  <Dropdown
                    value={formData.presidente_id}
                    options={parlamentares.map((p) => ({
                      label: `${p.nome}${p.nome_parlamentar ? ` (${p.nome_parlamentar})` : ""} - ${p.partido || "Sem partido"}`,
                      value: p.id,
                    }))}
                    onChange={(e) =>
                      setFormData({ ...formData, presidente_id: e.value })
                    }
                    placeholder="Selecione o presidente"
                    filter
                    showClear
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Secretário</label>
                  <Dropdown
                    value={formData.secretario_id}
                    options={parlamentares.map((p) => ({
                      label: `${p.nome}${p.nome_parlamentar ? ` (${p.nome_parlamentar})` : ""} - ${p.partido || "Sem partido"}`,
                      value: p.id,
                    }))}
                    onChange={(e) =>
                      setFormData({ ...formData, secretario_id: e.value })
                    }
                    placeholder="Selecione o secretário"
                    filter
                    showClear
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Ata da Sessão</label>
                  <InputTextarea
                    rows={5}
                    value={formData.ata}
                    onChange={(e) =>
                      setFormData({ ...formData, ata: e.target.value })
                    }
                    placeholder="Registro da ata da sessão..."
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Observações</label>
                  <InputTextarea
                    rows={3}
                    value={formData.observacoes}
                    onChange={(e) =>
                      setFormData({ ...formData, observacoes: e.target.value })
                    }
                    placeholder="Informações adicionais sobre a sessão..."
                  />
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog
        visible={viewModalVisible}
        style={{ width: "950px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setViewModalVisible(false)}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i className="pi pi-info-circle text-primary text-xl"></i>
            </div>
            <span className="font-bold text-xl">Detalhes da Sessão</span>
          </div>
        }
        footer={
          <div className="flex justify-content-between gap-2">
            <Button
              label="Fechar"
              size="small"
              severity="secondary"
              icon="pi pi-times"
              onClick={() => setViewModalVisible(false)}
              outlined
            />
            <Button
              label="Editar"
              size="small"
              severity="success"
              icon="pi pi-pencil"
              onClick={() => {
                setViewModalVisible(false);
                abrirModalEditar(sessaoSelecionada);
              }}
            />
          </div>
        }
      >
        {sessaoSelecionada && (
          <div style={{ padding: "0" }}>
            {/* Cabeçalho com número e data */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #3b82f6",
                padding: "15px",
                borderRadius: "8px 8px 0 0",
                marginBottom: "15px",
              }}
            >
              <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h2 style={{ margin: 0, color: "#1e293b" }}>
                    Sessão nº {sessaoSelecionada.numero}
                  </h2>
                  {sessaoSelecionada.local && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.85rem",
                        color: "#64748b",
                        marginTop: "5px",
                      }}
                    >
                      <span>{sessaoSelecionada.local}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-column align-items-end gap-2">
                  {(() => {
                    const status = statusSessao.find(
                      (s) => s.valor === sessaoSelecionada.status,
                    );
                    return (
                      <Tag
                        value={status?.label || sessaoSelecionada.status}
                        severity={status?.severity || "secondary"}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Informações da Mesa */}
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "8px",
                }}
              >
                <FaUserTie size={20} style={{ color: "#3b82f6" }} />
                <div>
                  <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    Presidente
                  </div>
                  <div style={{ fontWeight: "600", color: "#1e293b" }}>
                    {sessaoSelecionada.presidente_nome || "Não informado"}
                  </div>
                </div>
                {sessaoSelecionada.secretario_nome && (
                  <div style={{ marginLeft: "20px" }}>
                    <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                      Secretário
                    </div>
                    <div style={{ fontWeight: "600", color: "#1e293b" }}>
                      {sessaoSelecionada.secretario_nome}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Grid de informações */}
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "15px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <FaCalendarAlt style={{ color: "#64748b" }} />
                  <div>
                    <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                      Data
                    </div>
                    <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                      {new Date(sessaoSelecionada.data).toLocaleDateString(
                        "pt-BR",
                      )}
                    </div>
                  </div>
                </div>

                {sessaoSelecionada.hora_inicio && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaClock style={{ color: "#64748b" }} />
                    <div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        Horário
                      </div>
                      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                        {sessaoSelecionada.hora_inicio}{" "}
                        {sessaoSelecionada.hora_fim &&
                          `às ${sessaoSelecionada.hora_fim}`}
                      </div>
                    </div>
                  </div>
                )}

                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <FaCheckCircle style={{ color: "#64748b" }} />
                  <div>
                    <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                      Tipo
                    </div>
                    <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                      {tiposSessao.find(
                        (t) => t.valor === sessaoSelecionada.tipo,
                      )?.label || sessaoSelecionada.tipo}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ata */}
            {sessaoSelecionada.ata && (
              <div style={{ padding: "0 15px", marginBottom: "15px" }}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#64748b",
                    marginBottom: "5px",
                  }}
                >
                  Ata da Sessão
                </div>
                <div
                  style={{
                    lineHeight: "1.5",
                    color: "#334155",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {sessaoSelecionada.ata}
                </div>
              </div>
            )}

            {/* Observações */}
            {sessaoSelecionada.observacoes && (
              <div style={{ padding: "0 15px", marginBottom: "15px" }}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#64748b",
                    marginBottom: "5px",
                  }}
                >
                  Observações
                </div>
                <div style={{ lineHeight: "1.5", color: "#334155" }}>
                  {sessaoSelecionada.observacoes}
                </div>
              </div>
            )}

            {sessaoSelecionada.status === 'encerrada' && (
            <div style={{ padding: "0 15px", marginBottom: "15px" }}>
              <div style={{ 
                padding: "10px", 
                backgroundColor: sessaoSelecionada.pauta_aprovada === 1 ? "#dcfce7" : "#fee2e2", 
                borderRadius: "8px", 
                borderLeft: `4px solid ${sessaoSelecionada.pauta_aprovada === 1 ? "#10b981" : "#ef4444"}` 
              }}>
                <div style={{ fontSize: "0.8rem", color: sessaoSelecionada.pauta_aprovada === 1 ? "#166534" : "#991b1b" }}>
                  Resultado da Pauta
                </div>
                <div style={{ fontWeight: "600", color: sessaoSelecionada.pauta_aprovada === 1 ? "#14532d" : "#7f1d1d" }}>
                  {sessaoSelecionada.pauta_aprovada === 1 ? "Pauta Aprovada" : "Pauta Rejeitada"}
                </div>
              </div>
            </div>
          )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default SessoesPlenarias;
